import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { userMessage } from '../../api/client';
import { useFocusTrap } from '../../lib/useFocusTrap';
import { cls as formCls } from '../formStyles';

const ICON_STROKE = 1.75;

/** A card taller than the viewport scrolls with the OVERLAY, not inside itself (#1764 visual
 *  check: at 390x844 the invite dialog's title and footer sat off-screen with no way to reach
 *  them). Three parts, each a no-op for a card that fits, so a desktop dialog renders where it
 *  always did:
 *   - the overlay scrolls (`overflow-y-auto`) and keeps a 1rem gutter top and bottom (`py-4`);
 *   - the card centres itself with `m-auto` instead of the overlay's `items-center`. A flex
 *     child centred by `items-center` overflows BOTH ends, and the top end cannot be scrolled
 *     to; an auto margin centres when there is room and collapses to zero when there is not;
 *   - the scrim is `fixed`, so it still covers the viewport once the overlay has scrolled.
 *  Not `max-h` + `overflow-y-auto` on the card: that would clip any popover a dialog opens
 *  inside its card, at every call site. `Modal.test.tsx` pins the three classes. */
const overlay = 'fixed inset-0 z-[60] flex overflow-y-auto px-4 py-4';
const scrim   = 'fixed inset-0 bg-ink/40';
const cardBase = 'relative m-auto w-full rounded-token border border-line bg-surface p-6 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.3)]';
/** Every dialog's width until #1542: 21 non-test call sites rely on this exact figure, so it
 *  stays the default rather than becoming a per-caller judgment call. */
const DEFAULT_MAX_WIDTH_CLASS = 'max-w-[380px]';
const closeBtn = 'absolute right-3 top-3 flex h-8 w-8 max-sm:right-1 max-sm:top-1 max-sm:h-11 max-sm:w-11 cursor-pointer items-center justify-center rounded-token border-none bg-transparent text-slate transition-colors duration-150 hover:text-navy disabled:opacity-50 disabled:cursor-not-allowed';
const title    = 'font-serif text-[17px] font-normal text-navy mb-1 pr-6 max-sm:pr-10';
const subtitle = 'text-[12px] text-slate font-sans mb-4';
const input    = formCls.input;
const errorCls = 'mt-3 rounded-token bg-danger/8 px-3 py-2 text-[12px] text-danger font-sans';
const primary  = 'max-sm:min-h-11 px-5 py-2 text-[11px] font-bold rounded-token border-none cursor-pointer bg-navy text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed';
const ghost    = 'max-sm:min-h-11 px-4 py-2 text-[11px] font-semibold rounded-token border border-line-strong cursor-pointer bg-transparent text-navy uppercase tracking-[0.08em] font-sans';
const danger   = 'max-sm:min-h-11 px-5 py-2 text-[11px] font-bold rounded-token border-none cursor-pointer bg-danger text-cream uppercase tracking-[0.1em] font-sans disabled:opacity-50 disabled:cursor-not-allowed';

/** Quiet-Luxury modal shell: scrim + centered card, Escape/backdrop/Close-button to
 *  dismiss. All folder dialogs share it so colors/fonts flip with the theme
 *  automatically. `dismissable` (default true, backward compatible) gates all three
 *  dismiss paths — pass false while an in-flight action shouldn't be abandonable
 *  (ConfirmModal does this while its confirm is pending).
 *
 *  `maxWidthClassName` (steward-app#1542) is opt-in and defaults to `DEFAULT_MAX_WIDTH_CLASS`,
 *  today's `380px`, so the 21 non-test call sites that do not pass it render byte-identically.
 *  Only `AdvisorReview.tsx`'s connect dialog passes a wider value, because its own body
 *  clamp was widened for the same reason (see that file). A caller passes a single Tailwind
 *  `max-w-[...]` class, not a raw pixel number, so it stays a plain string substitution here
 *  rather than a second place that has to agree with Tailwind's arbitrary-value syntax. */
export function Modal({ titleText, subtitleText, onClose, children, ariaLabel, dismissable = true, maxWidthClassName = DEFAULT_MAX_WIDTH_CLASS }: {
  titleText: string;
  /** A node rather than a string, so a caller quoting a record's name in the sentence can
   *  wrap it in `<UserText>` and keep it from rearranging the words around it
   *  (steward-app#600). `titleText` stays a string: it is ours, and it is also the
   *  dialog's `aria-label` fallback, which has to be text. */
  subtitleText?: React.ReactNode;
  onClose: () => void;
  ariaLabel?: string;
  dismissable?: boolean;
  /** A Tailwind `max-w-[...]` class. Defaults to today's `380px`; see the docblock above. */
  maxWidthClassName?: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && dismissable) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, dismissable]);

  const trapRef = useFocusTrap<HTMLDivElement>(true);

  // Portalled to <body> so the dialog does not inherit its opener's text styles. Rendered
  // inline it took `text-right whitespace-nowrap` from the Prospects row's <td>, so the
  // message ran off the card in one line and the title floated (seen 2026-09-21).
  return createPortal((
    <div className={overlay} role="dialog" aria-modal="true" aria-label={ariaLabel ?? titleText} ref={trapRef}>
      <div className={scrim} onClick={() => { if (dismissable) onClose(); }} aria-hidden data-testid="modal-scrim" />
      <div className={`${cardBase} ${maxWidthClassName}`}>
        <button type="button" onClick={onClose} aria-label="Close" className={closeBtn} disabled={!dismissable}>
          <X size={16} strokeWidth={ICON_STROKE} aria-hidden />
        </button>
        <div className={title}>{titleText}</div>
        {subtitleText && <div className={subtitle}>{subtitleText}</div>}
        {children}
      </div>
    </div>
  ), document.body);
}

/** Single-line text prompt (replaces window.prompt for New/Rename folder). */
export function PromptModal({ titleText, subtitleText, label, initialValue = '', confirmLabel = 'Save', maxLength, isBlank = (v: string) => !v.trim(), onSubmit, onClose }: {
  titleText: string;
  /** Passed straight through to `Modal`, so it is a node for the same reason. */
  subtitleText?: React.ReactNode;
  label: string;
  initialValue?: string;
  confirmLabel?: string;
  /** The input's `maxLength`. Absent means no limit, as before it existed. */
  maxLength?: number;
  /** Whether a value counts as no name at all. Defaults to empty after trimming. */
  isBlank?: (value: string) => boolean;
  onSubmit: (value: string) => Promise<void> | void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initialValue);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();

  useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const v = value.trim();
    if (!v || isBlank(v)) { setErr('Please enter a name.'); return; }
    setBusy(true); setErr('');
    try {
      await onSubmit(v);
      onClose();
    } catch (e2) {
      setErr(userMessage(e2, 'Something went wrong.'));
      setBusy(false);
    }
  }

  return (
    <Modal titleText={titleText} subtitleText={subtitleText} onClose={onClose}>
      <form onSubmit={submit}>
        <label className="block text-[10px] font-semibold text-slate mb-1.5 uppercase tracking-[0.1em] font-sans" htmlFor={inputId}>{label}</label>
        <input id={inputId} ref={inputRef} className={input} value={value} maxLength={maxLength} onChange={e => setValue(e.target.value)} />
        {err && <div className={errorCls}>{err}</div>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className={ghost} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className={primary} disabled={busy || !value.trim()}>{busy ? 'Saving…' : confirmLabel}</button>
        </div>
      </form>
    </Modal>
  );
}

/** Destructive confirmation (replaces window.confirm for deletes).
 *
 *  `confirmDisabled` holds the confirm button while the dialog is still working out what
 *  it needs to tell the user (the disconnect dialog fetches what it would destroy, #352).
 *  Cancel and every dismiss path stay live: this delays a destructive yes, never a no.
 *
 *  ⚠️ **`cancelLabel` defaults to "Cancel" and stays that for every call site but two**
 *  (steward-app#1640 added the prop for `YourFirm.tsx`'s "Cancel invitation" dialog, whose
 *  own confirm button is ALSO "Cancel invitation" — "Cancel" as the dismiss would have been
 *  the same word as the destructive confirm, so it passes `cancelLabel="Keep it"`.
 *  steward-app#1644 gave People → Advisors' withdraw dialogs the identical prop for the
 *  identical reason on a different pair: their confirm is "Withdraw request" / "Withdraw
 *  invitation", and "Cancel" beside "Withdraw" reads as two words for the same act, one
 *  refusing it and one meaning it, in a dialog whose whole job is telling those two apart —
 *  so those also pass `cancelLabel="Keep it"`. No other `ConfirmModal` call site has this
 *  clash (an End access dialog's confirm is "End access", which does not overlap "Cancel" at
 *  all), so this is a prop with two non-default callers rather than a change to the shared
 *  default. */
export function ConfirmModal({ titleText, message, confirmLabel = 'Delete', cancelLabel = 'Cancel', tone = 'danger', confirmDisabled = false, onConfirm, onClose }: {
  titleText: string;
  /** A node, for the same reason as `Modal`'s `subtitleText` — a confirmation quoting a
   *  record's name is exactly where the reader needs to be sure which record it is. */
  message: React.ReactNode;
  confirmLabel?: string;
  /** The dismiss button's word, default `'Cancel'`. A caller confirming something that is
   *  not itself a cancellation (e.g. "Withdraw request", "Cancel invitation") can say what
   *  backing out actually does instead ("Keep it") rather than force "Cancel" to serve double
   *  duty as both this dialog's own dismiss and (in "Cancel invitation"'s case) its confirm's
   *  own word. */
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  confirmDisabled?: boolean;
  onConfirm: () => Promise<void> | void;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function confirm() {
    setBusy(true); setErr('');
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setErr(userMessage(e, 'Something went wrong.'));
      setBusy(false);
    }
  }

  return (
    <Modal titleText={titleText} onClose={onClose} dismissable={!busy}>
      <p className="text-[13px] text-body font-sans leading-relaxed m-0">{message}</p>
      {err && <div className={errorCls}>{err}</div>}
      <div className="mt-5 flex justify-end gap-2">
        <button type="button" className={ghost} onClick={onClose} disabled={busy}>{cancelLabel}</button>
        <button type="button" className={tone === 'danger' ? danger : primary} onClick={() => void confirm()} disabled={busy || confirmDisabled}>
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

/** Lightweight notice (replaces window.alert), e.g. "folder not empty". */
export function AlertModal({ titleText, message, onClose }: { titleText: string; message: React.ReactNode; onClose: () => void }) {
  return (
    <Modal titleText={titleText} onClose={onClose}>
      <p className="text-[13px] text-body font-sans leading-relaxed m-0">{message}</p>
      <div className="mt-5 flex justify-end">
        <button type="button" className={primary} onClick={onClose}>OK</button>
      </div>
    </Modal>
  );
}
